import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { ShoppingBag, FileText, X, ZoomIn, LogIn, CreditCard, Trash2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

export default function DigitalShop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [cart, setCart] = useState([]); // array of product ids
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const { format, exchangeRate } = useCurrency();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    document.title = "Digital Shop – Cheap4Teach";
    api.get("/digital-products")
      .then(r => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  // Convert any product price to JMD
  const toJmd = (p) =>
    (p.currency || "USD").toUpperCase() === "JMD"
      ? Number(p.price)
      : Math.round(Number(p.price) * (exchangeRate || 160));

  const toggleCart = (id) => {
    setCart(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const cartProducts = useMemo(
    () => products.filter(p => cart.includes(p.id)),
    [products, cart]
  );

  const cartJmdTotal = useMemo(
    () => cartProducts.reduce((sum, p) => sum + toJmd(p), 0),
    [cartProducts, exchangeRate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleWiPayCheckout = async () => {
    if (!user) {
      toast.error("Please sign in to purchase");
      nav("/login");
      return;
    }
    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setPaying(true);
    try {
      const r = await api.post("/wipay/digital/create-payment", {
        product_ids: cart,
      });
      window.location.href = r.data.wipay_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not initiate payment. Please try again.");
      setPaying(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-8 h-8 text-[#FF1F87]" />
            <h1 className="text-4xl sm:text-5xl font-['Fredoka'] font-bold">Digital Shop</h1>
          </div>
          <p className="c4t-desc text-lg">Downloadable PDF resources. Buy once, download instantly.</p>
          {!user && (
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#FFF9F2] border-2 border-[#FFC500] rounded-2xl text-sm font-semibold">
              <LogIn className="w-4 h-4 text-[#FFC500]" />
              <Link
                to="/login"
                onClick={() => localStorage.setItem("c4t_after_login", "/digital-shop")}
                className="text-[#118AB2] hover:text-[#FF477E]"
              >
                Sign in to purchase
              </Link>
            </div>
          )}
        </div>

        {/* Cart button */}
        {cart.length > 0 && (
          <button
            onClick={() => setCheckoutOpen(true)}
            className="c4t-btn c4t-btn-pink flex items-center gap-2 relative"
          >
            <ShoppingCart className="w-5 h-5" />
            Checkout
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#073B4C] text-white text-xs font-bold rounded-full flex items-center justify-center">
              {cart.length}
            </span>
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="c4t-card text-center py-20">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-xl font-['Fredoka'] font-bold text-gray-400">Digital products coming soon!</p>
          <p className="c4t-desc mt-2">Check back shortly.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map(p => {
            const imgSrc = p.cover_image_watermarked || p.cover_image || null;
            const inCart = cart.includes(p.id);

            return (
              <div key={p.id} className={`c4t-card flex flex-col transition-all ${inCart ? "ring-2 ring-[#FF1F87]" : ""}`}>
                {/* Cover image */}
                <div
                  className="relative aspect-square rounded-xl overflow-hidden bg-[#FFF9F2] border-2 border-[#073B4C]/10 mb-3 flex items-center justify-center group cursor-pointer"
                  onClick={() => imgSrc && setLightbox({ src: imgSrc, name: p.name })}
                >
                  {imgSrc ? (
                    <>
                      <img src={imgSrc} alt={p.name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
                          <ZoomIn className="w-5 h-5 text-[#073B4C]" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <FileText className="w-16 h-16 text-[#FF1F87]/30" />
                  )}
                  <span className="absolute top-2 left-2 px-2 py-1 bg-[#FF1F87] text-white text-xs font-['Fredoka'] font-bold rounded-full">PDF</span>
                </div>

                <div className="flex-1">
                  <h3 className="font-['Fredoka'] font-bold text-lg leading-tight">{p.name}</h3>
                  <p className="c4t-desc text-sm mt-1 line-clamp-2">{p.description}</p>
                  <p className="text-[#FF1F87] font-bold text-xl mt-2">{format(toJmd(p))}</p>
                </div>

                {/* Add to cart / remove */}
                <div className="mt-3">
                  {!user ? (
                    <Link
                      to="/login"
                      onClick={() => localStorage.setItem("c4t_after_login", "/digital-shop")}
                      className="c4t-btn !bg-[#0070ba] !border-[#003087] text-white w-full flex items-center justify-center gap-2 !py-2 !text-sm !no-underline"
                    >
                      <LogIn className="w-4 h-4" /> Sign in to Buy
                    </Link>
                  ) : inCart ? (
                    <button
                      onClick={() => toggleCart(p.id)}
                      className="w-full c4t-btn !bg-[#06D6A0] !border-[#04a07a] text-white flex items-center justify-center gap-2 !py-2 !text-sm"
                    >
                      <ShoppingBag className="w-4 h-4" /> Added ✓
                    </button>
                  ) : (
                    <button
                      onClick={() => toggleCart(p.id)}
                      className="w-full c4t-btn c4t-btn-pink flex items-center justify-center gap-2 !py-2 !text-sm"
                    >
                      <ShoppingBag className="w-4 h-4" /> Add to Cart
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Floating cart bar */}
      {cart.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 c4t-card !py-3 !px-5 shadow-2xl flex items-center gap-4 border-2 border-[#FF1F87]">
          <span className="font-['Fredoka'] font-bold text-lg">{cart.length} item{cart.length > 1 ? "s" : ""}</span>
          <span className="text-[#FF1F87] font-bold text-xl">{format(cartJmdTotal)}</span>
          <button
            onClick={() => setCheckoutOpen(true)}
            className="c4t-btn c4t-btn-pink flex items-center gap-2 !py-2"
          >
            <CreditCard className="w-4 h-4" /> Checkout
          </button>
        </div>
      )}

      {/* Checkout modal */}
      {checkoutOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => !paying && setCheckoutOpen(false)}
        >
          <div className="c4t-card max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-['Fredoka'] font-bold">Your Cart</h2>
              {!paying && (
                <button onClick={() => setCheckoutOpen(false)} className="text-gray-400 hover:text-[#FF477E]">
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>

            <div className="space-y-3 mb-5">
              {cartProducts.map(p => (
                <div key={p.id} className="flex items-center justify-between gap-3 bg-[#FFF9F2] rounded-xl px-3 py-2">
                  <div className="flex-1">
                    <p className="font-semibold text-sm leading-tight">{p.name}</p>
                    <p className="text-[#FF1F87] font-bold text-sm">{format(toJmd(p))}</p>
                  </div>
                  {!paying && (
                    <button
                      onClick={() => {
                        toggleCart(p.id);
                        if (cart.length <= 1) setCheckoutOpen(false);
                      }}
                      className="text-gray-400 hover:text-[#FF477E]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between border-t-2 border-dashed border-[#073B4C]/10 pt-4 mb-5">
              <span className="font-['Fredoka'] font-bold text-lg">Total</span>
              <span className="text-[#FF1F87] font-bold text-2xl">{format(cartJmdTotal)}</span>
            </div>

            <div className="bg-[#FFF9F2] border border-[#FFC500] rounded-xl p-3 mb-5 text-xs text-[#455A64]">
              <span className="font-bold text-[#073B4C]">Secure card payment via WiPay.</span> You'll be redirected to a hosted payment page. Download links will be sent to your email and available in My Orders.
            </div>

            <button
              onClick={handleWiPayCheckout}
              disabled={paying}
              className="w-full c4t-btn c4t-btn-pink flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {paying ? (
                <>
                  <div className="w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin" />
                  Redirecting…
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Pay by Card (WiPay)
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-10 right-0 text-white hover:text-[#FFC500] transition-colors"
              aria-label="Close"
            >
              <X className="w-8 h-8" />
            </button>
            <img src={lightbox.src} alt={lightbox.name} className="w-full rounded-2xl border-4 border-white shadow-2xl" />
            <p className="text-white text-center mt-3 font-['Fredoka'] font-bold text-lg">{lightbox.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}
